import React, { useCallback, useEffect, useRef, useState } from 'react';
import './MessageAttachments.css';

// Lazy-loaded PDF.js. We import dynamically inside PdfThumbnail so the
// ~1 MB pdfjs bundle isn't pulled in for users who never see a PDF
// attachment, and so the worker URL setup runs once at first use.
let _pdfjsLibPromise = null;
function loadPdfJs() {
  if (_pdfjsLibPromise) return _pdfjsLibPromise;
  _pdfjsLibPromise = (async () => {
    // Legacy build is the safest bet across older browsers + CRA's webpack 5.
    const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
    // Worker hosted on jsdelivr at the exact same version we bundled. Falls
    // back gracefully if the CDN is blocked — the thumbnail just renders
    // the chip placeholder.
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;
    return pdfjsLib;
  })();
  return _pdfjsLibPromise;
}

/**
 * MessageAttachments — renders the inline attachment preview strip on a
 * message bubble. Images become click-to-expand thumbnails (light-box on
 * click); PDFs become chip-style download links that open in a new tab.
 *
 * The `url` field on each attachment is a short-lived (1h) presigned S3 GET
 * URL minted by the backend resolver. Stale URLs are an expected failure mode
 * — when an <img> errors we surface a small reload affordance that triggers
 * the parent Apollo cache refresh.
 */
function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ImageAttachment({ attachment, onOpen }) {
  const [errored, setErrored] = useState(false);

  const handleClick = useCallback(() => {
    if (!errored) onOpen(attachment);
  }, [attachment, errored, onOpen]);

  // When the image finishes loading, fire a bubbling DOM event so the
  // surrounding message list can re-snap to the bottom if the user is
  // already there. Without this, the initial scroll-on-new-message lands
  // before the image's intrinsic height is known and the user sees a
  // partially-clipped image.
  const handleLoad = useCallback((event) => {
    const node = event?.currentTarget;
    if (!node) return;
    node.dispatchEvent(
      new CustomEvent('kapsa-attachment-image-loaded', {
        bubbles: true,
        detail: { fileName: attachment.fileName, key: attachment.key }
      })
    );
  }, [attachment.fileName, attachment.key]);

  if (errored || !attachment.url) {
    return (
      <div className="msg-attach msg-attach--errored" title={attachment.fileName}>
        <span className="msg-attach__icon" aria-hidden="true">🖼️</span>
        <span className="msg-attach__name">{attachment.fileName}</span>
        <span className="msg-attach__hint">image unavailable</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="msg-attach msg-attach--image"
      onClick={handleClick}
      aria-label={`Open ${attachment.fileName}`}
    >
      <img
        src={attachment.url}
        alt={attachment.fileName}
        onError={() => setErrored(true)}
        onLoad={handleLoad}
        loading="lazy"
      />
    </button>
  );
}

/**
 * PdfThumbnail renders the first page of a PDF to a canvas using PDF.js. The
 * canvas overlays the filename + size so users get the same affordance as a
 * file chip but with real content. The whole thing is wrapped in an <a> so
 * clicking opens the full PDF in a new tab. On any failure (CDN-blocked
 * worker, malformed PDF, presigned URL expired) we fall back to a chip.
 */
function PdfThumbnail({ attachment }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | failed

  useEffect(() => {
    let cancelled = false;
    let task = null; // pdfjsLib loadingTask, so we can destroy it on unmount

    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        if (cancelled) return;
        task = pdfjsLib.getDocument({ url: attachment.url });
        const doc = await task.promise;
        if (cancelled) return;
        const page = await doc.getPage(1);
        if (cancelled) return;
        // Render at a width that comfortably fills our 200px-wide tile while
        // staying readable on retina displays.
        const targetWidth = 200;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = targetWidth / baseViewport.width;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!cancelled) {
          setStatus('ready');
          // Re-snap the chat list now that we have a known thumbnail size,
          // matching the auto-scroll behavior of <img onLoad>.
          canvas.dispatchEvent(
            new CustomEvent('kapsa-attachment-image-loaded', {
              bubbles: true,
              detail: { fileName: attachment.fileName, key: attachment.key }
            })
          );
        }
      } catch (err) {
        if (!cancelled) {
          // Don't surface a console.error: PDF preview is non-essential and
          // the chip fallback is acceptable UX.
          setStatus('failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        task?.destroy?.();
      } catch (_) { /* ignore */ }
    };
  }, [attachment.url, attachment.fileName, attachment.key]);

  if (!attachment.url) {
    return (
      <div className="msg-attach msg-attach--errored">
        <span className="msg-attach__icon" aria-hidden="true">📄</span>
        <span className="msg-attach__name">{attachment.fileName}</span>
        <span className="msg-attach__hint">unavailable</span>
      </div>
    );
  }

  if (status === 'failed') {
    // Chip fallback — the original UX before client-side rendering.
    return (
      <a
        className="msg-attach msg-attach--pdf"
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        title={`${attachment.fileName} (${formatBytes(attachment.sizeBytes)})`}
      >
        <span className="msg-attach__icon" aria-hidden="true">📄</span>
        <span className="msg-attach__name">{attachment.fileName}</span>
        <span className="msg-attach__hint">{formatBytes(attachment.sizeBytes)}</span>
      </a>
    );
  }

  return (
    <a
      className="msg-attach msg-attach--pdf-thumb"
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${attachment.fileName} (${formatBytes(attachment.sizeBytes)})`}
    >
      <div className="msg-attach__pdf-canvas-wrap">
        <canvas ref={canvasRef} className="msg-attach__pdf-canvas" />
        {status === 'loading' && (
          <div className="msg-attach__pdf-skeleton" aria-hidden="true">📄</div>
        )}
      </div>
      <div className="msg-attach__pdf-meta">
        <span className="msg-attach__name">{attachment.fileName}</span>
        <span className="msg-attach__hint">{formatBytes(attachment.sizeBytes)}</span>
      </div>
    </a>
  );
}

function Lightbox({ attachment, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!attachment) return null;
  return (
    <div className="msg-attach-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <img
        src={attachment.url}
        alt={attachment.fileName}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        className="msg-attach-lightbox__close"
        onClick={onClose}
        aria-label="Close preview"
      >
        ×
      </button>
    </div>
  );
}

export default function MessageAttachments({ attachments }) {
  const [active, setActive] = useState(null);
  // Hooks must run before any early return (rules-of-hooks).
  const close = useCallback(() => setActive(null), []);

  if (!Array.isArray(attachments) || attachments.length === 0) return null;

  return (
    <>
      <div className="msg-attach-grid">
        {attachments.map((att) => {
          if (!att?.key) return null;
          if (att.kind === 'image') {
            return (
              <ImageAttachment
                key={att.key}
                attachment={att}
                onOpen={setActive}
              />
            );
          }
          return <PdfThumbnail key={att.key} attachment={att} />;
        })}
      </div>
      {active && <Lightbox attachment={active} onClose={close} />}
    </>
  );
}
